import React from 'react';
import { useAudioPlayer } from '../../hooks/useAudioPlayer';
import Icon from '../AppIcon';
import {
  Tree,
  Waves,
  Drop,
  CloudRain,
  Sun,
  ArrowDown,
  Check,
} from '@phosphor-icons/react';
import { AMBIENT_TRACKS } from '../../services/ambientSoundService';

// Format seconds to MM:SS
function formatTime(secs) {
  if (!secs || isNaN(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s?.toString()?.padStart(2, '0')}`;
}

// Format voice name: 'en-US-Chirp3-HD-Charon' → 'Charon (US)'
function formatVoiceName(voice) {
  if (!voice) return voice;
  const parts = voice?.split('-');
  const name = parts?.[parts?.length - 1];
  const locale = parts?.[1] || '';
  return `${name} (${locale})`;
}

const SPEEDS = [0.5, 0.75, 1, 1.5, 2];

// Small toggle switch component
function Toggle({ checked, onChange, label }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
        checked ? 'bg-amber-500' : 'bg-white/20'
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// Volume slider with percentage label
function VolumeSlider({ label, value, onChange, icon }) {
  return (
    <div className="flex items-center gap-2">
      <Icon name={icon} size={13} className="text-white/40 shrink-0" />
      <span className="text-white/50 text-xs w-20 shrink-0">{label}</span>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={Math.round(value * 100)}
        onChange={(e) => onChange(parseInt(e?.target?.value, 10) / 100)}
        className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, #F59E0B ${value * 100}%, rgba(255,255,255,0.15) ${value * 100}%)`,
          accentColor: '#F59E0B',
        }}
      />
      <span className="text-white/60 text-xs w-8 text-right shrink-0">
        {Math.round(value * 100)}%
      </span>
    </div>
  );
}

// Map track icon string → Phosphor component
const PHOSPHOR_ICON_MAP = {
  Tree,
  Waves,
  Drop,
  CloudRain,
  Sun,
  ArrowDown,
};

function PhosphorTrackIcon({ name, size = 18, className }) {
  const Comp = PHOSPHOR_ICON_MAP?.[name];
  if (!Comp) return null;
  return <Comp size={size} className={className} weight="duotone" />;
}

export default function AudioPlayerDrawer() {
  const {
    isOpen, isPlaying, isLoaded, isLoading, loadError,
    currentTime, duration, playbackSpeed, loopMode, autoAdvance, chapterComplete,
    activeBook, activeChapter, activeVoice, availableVoices,
    hasChunkOffsetBug,
    isAtStart,
    // Ambient
    ambientEnabled, ambientType, chapterVolume, ambientVolume,
    closeDrawer, play, pause, stop, seek, setSpeed,
    setLoopMode, setAutoAdvance, setVoice,
    nextChapter, prevChapter, rewind, fastForward,
    toggleAmbient, setAmbientType, updateAmbientVolume, updateChapterVolume,
  } = useAudioPlayer();

  const handleSeekChange = (e) => {
    seek(parseFloat(e?.target?.value));
  };

  const handleLoopChapter = () => {
    if (loopMode === 'chapter') {
      setLoopMode('none');
    } else {
      setLoopMode('chapter');
      setAutoAdvance(false);
    }
  };

  const handleLoopVerse = () => {
    if (loopMode === 'verse') {
      setLoopMode('none');
    } else {
      setLoopMode('verse');
      setAutoAdvance(false);
    }
  };

  const handleAutoAdvance = () => {
    const next = !autoAdvance;
    setAutoAdvance(next);
    if (next) setLoopMode('none');
  };

  return (
    <>
      {/* Backdrop on mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden animate-in fade-in duration-200"
          onClick={closeDrawer}
        />
      )}
      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col shadow-2xl transition-transform duration-300 ease-in-out
          w-full sm:w-80
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ background: '#1a1a2e' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10"
          style={{ background: 'linear-gradient(to right, rgba(245,158,11,0.18), rgba(245,158,11,0.06), transparent)' }}>
          <div>
            <h2 className="text-white font-semibold text-base leading-tight">
              {activeBook || 'Bible Audio'}
            </h2>
            {activeChapter && (
              <p className="text-amber-400 text-sm mt-0.5">Chapter {activeChapter}</p>
            )}
          </div>
          <button
            onClick={closeDrawer}
            className="w-8 h-8 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close player"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Chunk offset bug warning banner */}
          {hasChunkOffsetBug && !isLoading && (
            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-amber-500/15 border border-amber-500/40">
              <Icon name="AlertTriangle" size={16} className="text-amber-400 shrink-0" />
              <p className="text-amber-300 text-xs leading-snug">
                Verse tracking unavailable — this chapter needs to be regenerated.
              </p>
            </div>
          )}

          {/* Loading / Error state */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-8 space-y-3">
              <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-white/60 text-sm">Loading audio...</p>
            </div>
          )}

          {loadError && !isLoading && (
            <div className="flex flex-col items-center justify-center py-8 space-y-2">
              <Icon name="VolumeX" size={32} className="text-white/30" />
              <p className="text-white/60 text-sm text-center">No audio available for this chapter.</p>
            </div>
          )}

          {!isLoading && !loadError && (
            <>
              {/* Voice selector */}
              {availableVoices?.length > 1 && (
                <div>
                  <label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Voice</label>
                  <select
                    value={activeVoice}
                    onChange={(e) => setVoice(e?.target?.value)}
                    className="w-full bg-white/10 text-white border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
                  >
                    {availableVoices?.map(v => (
                      <option key={v} value={v} style={{ background: '#1a1a2e' }}>
                        {formatVoiceName(v)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Progress */}
              <div>
                <div className="flex justify-between text-white/50 text-xs mb-2">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={duration || 0}
                  step={0.5}
                  value={currentTime}
                  onChange={handleSeekChange}
                  disabled={!isLoaded}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: duration
                      ? `linear-gradient(to right, #F59E0B ${(currentTime / duration) * 100}%, rgba(255,255,255,0.15) ${(currentTime / duration) * 100}%)`
                      : 'rgba(255,255,255,0.15)',
                    accentColor: '#F59E0B',
                  }}
                />
              </div>

              {/* Chapter complete banner */}
              {chapterComplete && !autoAdvance && (
                <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-green-500/15 border border-green-500/40">
                  <Icon name="CheckCircle" size={16} className="text-green-400 shrink-0" />
                  <p className="text-green-300 text-xs leading-snug">
                    Chapter complete. Enable Auto-advance to continue.
                  </p>
                </div>
              )}

              {/* Main controls */}
              <div className="flex items-center justify-between">
                {/* Prev chapter */}
                <button
                  onClick={prevChapter}
                  disabled={isAtStart || !isLoaded}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous chapter"
                >
                  <Icon name="SkipBack" size={20} />
                </button>

                {/* Rewind 10s */}
                <button
                  onClick={rewind}
                  disabled={!isLoaded}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Rewind 10 seconds"
                >
                  <Icon name="RotateCcw" size={20} />
                </button>

                {/* Play/Pause */}
                <button
                  onClick={isPlaying ? pause : play}
                  disabled={!isLoaded}
                  className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-amber-500/30 active:scale-95"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  <Icon name={isPlaying ? 'Pause' : 'Play'} size={24} className="text-white" />
                </button>

                {/* Stop */}
                <button
                  onClick={stop}
                  disabled={!isLoaded}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Stop"
                >
                  <Icon name="Square" size={20} />
                </button>

                {/* Fast forward 10s */}
                <button
                  onClick={fastForward}
                  disabled={!isLoaded}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Fast forward 10 seconds"
                >
                  <Icon name="RotateCw" size={20} />
                </button>

                {/* Next chapter */}
                <button
                  onClick={nextChapter}
                  disabled={!isLoaded}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next chapter"
                >
                  <Icon name="SkipForward" size={20} />
                </button>
              </div>

              {/* Speed selector */}
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Speed</label>
                <div className="flex gap-1.5">
                  {SPEEDS?.map(s => (
                    <button
                      key={s}
                      onClick={() => setSpeed(s)}
                      className={`flex-1 py-1.5 rounded-full text-xs font-medium transition-all ${
                        playbackSpeed === s
                          ? 'bg-amber-500 text-white shadow-sm'
                          : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                      }`}
                    >
                      {s}×
                    </button>
                  ))}
                </div>
              </div>

              {/* Loop / Advance toggles */}
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">Playback Options</label>
                <div className="flex gap-2">
                  {/* Repeat chapter */}
                  <button
                    onClick={handleLoopChapter}
                    disabled={autoAdvance}
                    className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl border transition-all text-xs ${
                      loopMode === 'chapter' ?'bg-amber-500/20 border-amber-500 text-amber-400' :'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                    aria-label="Repeat chapter"
                  >
                    <Icon name="Repeat" size={16} />
                    <span>Chapter</span>
                  </button>

                  {/* Repeat verse */}
                  <button
                    onClick={handleLoopVerse}
                    disabled={autoAdvance}
                    className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl border transition-all text-xs ${
                      loopMode === 'verse' ?'bg-amber-500/20 border-amber-500 text-amber-400' :'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                    aria-label="Repeat verse"
                  >
                    <Icon name="Repeat1" size={16} />
                    <span>Verse</span>
                  </button>

                  {/* Auto-advance */}
                  <button
                    onClick={handleAutoAdvance}
                    className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl border transition-all text-xs ${
                      autoAdvance
                        ? 'bg-amber-500/20 border-amber-500 text-amber-400' :'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'
                    }`}
                    aria-label="Auto-advance to next chapter"
                  >
                    <Icon name="ListEnd" size={16} />
                    <span>Auto</span>
                  </button>
                </div>
              </div>

              {/* ── Volume Mixer ─────────────────────────────────────────── */}
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider mb-3 block">Volume Mix</label>
                <div className="space-y-3">
                  <VolumeSlider
                    label="Chapter"
                    value={chapterVolume}
                    onChange={updateChapterVolume}
                    icon="Volume2"
                  />
                  <VolumeSlider
                    label="Ambient"
                    value={ambientVolume}
                    onChange={updateAmbientVolume}
                    icon="Music"
                  />
                </div>
              </div>

              {/* ── Ambient Sound ─────────────────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-white/50 text-xs uppercase tracking-wider">Ambient Sound</label>
                  <Toggle
                    checked={ambientEnabled}
                    onChange={toggleAmbient}
                    label="Toggle ambient sound"
                  />
                </div>

                {ambientEnabled && (
                  <div className="space-y-2">
                    {AMBIENT_TRACKS?.map((track) => (
                      <button
                        key={track?.id}
                        onClick={() => setAmbientType(track?.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-sm ${
                          ambientType === track?.id
                            ? track?.activeColor
                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white/80'
                        }`}
                      >
                        <PhosphorTrackIcon
                          name={track?.icon}
                          size={18}
                          className={ambientType === track?.id ? '' : 'opacity-60'}
                        />
                        <div className="text-left flex-1">
                          <div className="font-medium text-xs">{track?.label}</div>
                          <div className="text-white/40 text-xs">{track?.description}</div>
                        </div>
                        {ambientType === track?.id && (
                          <Check size={14} className={track?.checkColor} weight="bold" />
                        )}
                      </button>
                    ))}

                    <p className="text-white/30 text-xs px-1 pt-1">
                      Ambient loops while audio is active and fades in/out smoothly.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
